<template>
    <v-row>
        <v-col cols="12" sm="6">
            <ValidatableTextField v-model:form="form" label="Property Name" :field="'property_name'" />
        </v-col>
        <v-col cols="12" sm="6">
            <ValidatableTextField v-model:form="form" label="Community" :field="'community'" />
        </v-col>
        <v-col cols="12" sm="6">
            <ValidatableTextField v-model:form="form" label="Municipality" :field="'municipality'" />
        </v-col>
        <v-col cols="12" sm="6">
            <ReadOnlyTextField :value="form.property_short_code" label="Short Code" />
        </v-col>
        <v-col cols="12" sm="6">
            <ReadOnlyTextField :value="form.property_code" label="Property Code" />
        </v-col>
        <v-col cols="12">
            <Autocomplete v-model:selected-items="form.owner_id" v-model:form="form" :field-name="'owner_id'" :items="owners" :loading="fetchingOwners" label="Owner" />
        </v-col>
        <v-col cols="12" sm="6">
            <Autocomplete v-model:selected-items="form.inventory_type_id" v-model:form="form" :field-name="'inventory_type_id'" :items="inventoryTypes" :loading="fetchingInventoryTypes" label="Inventory Type" />
        </v-col>
        <v-col cols="12" sm="6">
            <ValidatableTextField v-model:form="form" type="number" label="Queue Priority" :field="'priority'" />
        </v-col>
        <v-col cols="12" sm="6">
            <Autocomplete v-model:selected-items="form.steward_id" v-model:form="form" :field-name="'steward_id'" item-title="steward_code" :items="stewards" :loading="fetchingStewards" label="Steward" />
        </v-col>
        <v-col cols="12" sm="6">
            <Autocomplete v-model:selected-items="form.state_id" v-model:form="form" :field-name="'state_id'" item-title="_3_letter_code" :items="states" :loading="fetchingStates" label="State" />
        </v-col>
        <v-col cols="12" sm="6">
            <DatePicker v-model:field="form.start_date" v-model:form="form" label="Start Date" :field-name="'start_date'" />
        </v-col>
        <v-col cols="12" sm="6">
            <DatePicker v-model:field="form.end_date" v-model:form="form" label="End Date" :field-name="'end_date'" />
        </v-col>
        <v-col cols="12" sm="6">
            <ValidatableTextField v-model:form="form" min="0" type="number" label="Buffer Percentage" clearable :field="'buffer'" />
        </v-col>
        <v-col cols="12" sm="6">
            <ValidatableTextField v-model:form="form" min="0" type="number" label="Carbon Loss (tCO2e)" clearable :field="'carbon_loss'" />
        </v-col>
        <v-col cols="12">
            <ValidatableTextField v-model:form="form" label="Carbon Loss Comments" clearable :field="'additional_info'" />
        </v-col>
        <v-col cols="12">
            <Autocomplete v-model:selected-items="form.project_ids" field-name="project_ids" item-title="name" item-value="id" :items="projects" label="Projects" multiple :loading="fetchingProjects" />
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
import { type IProperty } from '@app/land-hold/property/models/Property';
import { useInjectSubmitForm } from '@shared/composables/action-dialog/useInjectSubmitForm';
import { usePropertyRepository } from '@app/land-hold/property/repositories/usePropertyRepository';
import type { ProjectsInclude } from '@app/land-hold/property/routes/routes.ts';
import { useProjectRepository } from '@app/land-hold/project/repositories/useProjectRepository.ts';
import type { IProject } from '@app/land-hold/project/models/Project.ts';
import { useStateRepository } from '@app/location/state/repositories/useStateRepository.ts';
import { useStewardRepository } from '@app/land-hold/steward/repositories/useStewardRepository.ts';
import { useOwnerRepository } from '@app/land-hold/owner/repositories/useOwnerRepository.ts';
import { useInventoryTypeRepository } from '@app/land-hold/inventory-type/repositories/useInventoryTypeRepository.ts';

const props = defineProps({
    item: {
        type: Object as PropType<IProperty & ProjectsInclude>,
        required: true,
    },
});

const update = usePropertyRepository<IProperty & { project_ids: string[] }>().command.update(props.item.id, {
    ...props.item,
    project_ids: [],
});
const form = update.form;
useInjectSubmitForm(update.execute);

const projects = ref<IProject[]>([]);
const fetchingProjects = ref(false);
let stopDataWatch: (() => void) | null = null;
let stopProcessingWatch: (() => void) | null = null;

watch(
    () => form.steward_id,
    (stewardId) => {
        stopDataWatch?.();
        stopProcessingWatch?.();

        if (stewardId) {
            const { data, processing } = useProjectRepository().query.listAllowedFor(stewardId);
            stopDataWatch = watch(
                data,
                (v) => {
                    const existing = stewardId === props.item.steward_id ? (props.item.projects?.data ?? []) : [];
                    const supplement = existing.filter((p) => !v.some((allowed) => allowed.id === p.id));
                    projects.value = [...supplement, ...v];
                },
                { immediate: true },
            );
            stopProcessingWatch = watch(
                processing,
                (v) => {
                    fetchingProjects.value = v;
                },
                { immediate: true },
            );

            if (stewardId === props.item.steward_id) {
                form.project_ids = props.item.projects.data.map((project) => project.id) ?? [];
            } else {
                form.project_ids = [];
            }
        } else {
            projects.value = [];
            fetchingProjects.value = false;
            form.project_ids = [];
        }
    },
    {
        immediate: true,
    },
);

onUnmounted(() => {
    stopDataWatch?.();
    stopProcessingWatch?.();
});
const { inventoryTypes, fetchingInventoryTypes } = useInventoryTypeRepository().query.list();
const { owners, fetchingOwners } = useOwnerRepository().query.list();
const { stewards, fetchingStewards } = useStewardRepository().query.list();
const { states, fetchingStates } = useStateRepository().query.list();
</script>
